import { useMutation } from '@tanstack/react-query'
import { Scan } from '@untitledui/icons'
import { lazy, Suspense } from 'react'

import StandaloneLoadingSpinner from '@components/StandaloneLoadingSpinner'
import Dialog from '@components/designSystem/Dialog/Dialog'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { useProductSearchByGTINImp } from '@state/product'

import type { BaseDialogProps } from '@components/designSystem/Dialog/DialogContext'

// lazy load the scanner component to improve initial load time
const Scanner = lazy(() => import('@components/barcodeScanner/BarcodeScanner'))

interface GtinScannerDialogProps extends BaseDialogProps {
  onScan: (gtin: string) => void
}

const GTIN_NOT_FOUND_ERROR_MESSAGE = 'GTIN not found'

export function GtinScannerDialog({ onScan, onClose }: GtinScannerDialogProps) {
  const { t } = useTypedTranslation()
  const searchByGtin = useProductSearchByGTINImp()

  const { mutate, error, reset } = useMutation({
    mutationFn: async (code: string) => {
      const gtinResult = await searchByGtin(code)
      const items = gtinResult.items

      if (items.length === 0 || items[0] === undefined) {
        throw new Error(GTIN_NOT_FOUND_ERROR_MESSAGE)
      }

      return items[0].gtin
    },
    onSuccess: (gtin) => {
      onScan(gtin)
      onClose?.()
    },
  })

  function inferErrorMsg() {
    if (!error) {
      return undefined
    }

    if (error.message === GTIN_NOT_FOUND_ERROR_MESSAGE) {
      return t('productFinder.barcodeScanner.scanHint.unknownGtin')
    }

    return t('productFinder.barcodeScanner.scanHint.error')
  }

  return (
    <Dialog
      headerIcon={Scan}
      title={t('productFinder.barcodeScanner.header')}
      size="md"
      onClose={onClose}
      className='min-h-[450px]'
    >
      <Suspense fallback={<StandaloneLoadingSpinner />}>
        <Scanner
          errorMsg={inferErrorMsg()}
          onDetected={mutate}
          onErrorDismiss={() => reset()}
        />
      </Suspense>
    </Dialog>
  )
}
